/**
 * Organizations Panel Module
 * 
 * Handles organization listing, member management, invitations, and policies.
 */

import { api, OrgInfo, OrgMember, OrgInvitation, OrgPolicies } from './api';

// ============================================================================
// State
// ============================================================================

let organizations: OrgInfo[] = [];
let selectedOrg: OrgInfo | null = null;
let currentMembers: OrgMember[] = [];
let currentInvitations: OrgInvitation[] = [];
let currentPolicies: OrgPolicies | null = null;

// DOM Elements
const orgList = document.getElementById('org-list')!;
const orgEmpty = document.getElementById('org-empty')!;
const orgListView = document.getElementById('org-list-view')!;
const orgDetailView = document.getElementById('org-detail-view')!;
const createOrgModal = document.getElementById('create-org-modal')!;
const inviteModal = document.getElementById('invite-modal')!;

// ============================================================================
// Initialization
// ============================================================================

export function initOrganizations(): void {
    // Create org button
    document.getElementById('create-org-btn')?.addEventListener('click', showCreateOrgModal);

    // Back button
    document.getElementById('org-back-btn')?.addEventListener('click', showOrgList);

    // Tab switching
    document.querySelectorAll('.org-tab').forEach(tab => {
        tab.addEventListener('click', () => {
            const tabName = (tab as HTMLElement).dataset.tab;
            if (tabName) switchTab(tabName);
        });
    });

    // Invite buttons
    document.getElementById('invite-member-btn')?.addEventListener('click', showInviteModal);
    document.getElementById('add-member-btn')?.addEventListener('click', showInviteModal);

    // Create org modal
    document.getElementById('create-org-modal-close')?.addEventListener('click', hideCreateOrgModal);
    document.getElementById('create-org-cancel')?.addEventListener('click', hideCreateOrgModal);
    document.getElementById('create-org-form')?.addEventListener('submit', handleCreateOrg);
    createOrgModal.querySelector('.modal-backdrop')?.addEventListener('click', hideCreateOrgModal);

    // Invite modal
    document.getElementById('invite-modal-close')?.addEventListener('click', hideInviteModal);
    document.getElementById('invite-cancel')?.addEventListener('click', hideInviteModal);
    document.getElementById('invite-form')?.addEventListener('submit', handleInvite);
    inviteModal.querySelector('.modal-backdrop')?.addEventListener('click', hideInviteModal);

    // Save policies
    document.getElementById('save-policies-btn')?.addEventListener('click', handleSavePolicies);
}

export async function refreshOrganizations(): Promise<void> {
    try {
        const response = await api.getOrganizations();
        organizations = response.organizations;
        renderOrgList();
    } catch (error) {
        console.error('Failed to load organizations:', error);
        organizations = [];
        renderOrgList();
    }
}

// ============================================================================
// Rendering
// ============================================================================

function renderOrgList(): void {
    if (organizations.length === 0) {
        orgList.innerHTML = '';
        orgEmpty.classList.remove('hidden');
        return;
    }

    orgEmpty.classList.add('hidden');

    orgList.innerHTML = organizations.map(org => `
        <div class="org-card" data-org-id="${org.id}">
            <div class="org-card-header">
                <div class="org-card-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M3 21h18" />
                        <path d="M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16" />
                    </svg>
                </div>
                <div class="org-card-info">
                    <h3>${escapeHtml(org.name)}</h3>
                    <span class="org-slug">${escapeHtml(org.slug)}</span>
                </div>
                <span class="badge badge-${org.plan}">${org.plan.toUpperCase()}</span>
            </div>
            <div class="org-card-stats">
                <span><strong>${org.member_count || 1}</strong> / ${org.max_seats} seats</span>
            </div>
        </div>
    `).join('');

    // Add click handlers
    orgList.querySelectorAll('.org-card').forEach(card => {
        card.addEventListener('click', () => {
            const orgId = (card as HTMLElement).dataset.orgId;
            const org = organizations.find(o => o.id === orgId);
            if (org) showOrgDetail(org);
        });
    });
}

async function showOrgDetail(org: OrgInfo): Promise<void> {
    selectedOrg = org;

    // Update header
    document.getElementById('org-detail-name')!.textContent = org.name;
    document.getElementById('org-detail-plan')!.textContent = org.plan.toUpperCase();
    document.getElementById('org-detail-plan')!.className = `badge badge-${org.plan}`;

    // Toggle views
    orgListView.classList.add('hidden');
    orgDetailView.classList.remove('hidden');

    // Reset to members tab
    switchTab('members');

    // Load data
    await Promise.all([
        loadMembers(),
        loadInvitations(),
        loadPolicies()
    ]);

    // Update stats
    updateOrgStats();
}

function showOrgList(): void {
    selectedOrg = null;
    orgDetailView.classList.add('hidden');
    orgListView.classList.remove('hidden');
}

function updateOrgStats(): void {
    const seatCount = document.getElementById('org-seat-count')!;
    const inviteCount = document.getElementById('org-invite-count')!;

    seatCount.textContent = `${currentMembers.length}/${selectedOrg?.max_seats || 0}`;
    inviteCount.textContent = String(currentInvitations.length);
}

// ============================================================================
// Tabs
// ============================================================================

function switchTab(tabName: string): void {
    // Update tab buttons
    document.querySelectorAll('.org-tab').forEach(tab => {
        const isActive = (tab as HTMLElement).dataset.tab === tabName;
        tab.classList.toggle('active', isActive);
    });

    // Update content
    document.querySelectorAll('.org-tab-content').forEach(content => {
        const isActive = content.id === `org-tab-${tabName}`;
        content.classList.toggle('active', isActive);
        content.classList.toggle('hidden', !isActive);
    });
}

// ============================================================================
// Members
// ============================================================================

async function loadMembers(): Promise<void> {
    if (!selectedOrg) return;

    try {
        const response = await api.getMembers(selectedOrg.id);
        currentMembers = response.members;
        renderMembers();
    } catch (error) {
        console.error('Failed to load members:', error);
        currentMembers = [];
        renderMembers();
    }
}

function renderMembers(): void {
    const tbody = document.getElementById('members-tbody')!;

    tbody.innerHTML = currentMembers.map(member => `
        <tr>
            <td><code>${escapeHtml(member.user_id.substring(0, 12))}...</code></td>
            <td>
                <select class="role-select" data-user-id="${member.user_id}" ${member.role === 'owner' ? 'disabled' : ''}>
                    <option value="owner" ${member.role === 'owner' ? 'selected' : ''}>Owner</option>
                    <option value="admin" ${member.role === 'admin' ? 'selected' : ''}>Admin</option>
                    <option value="member" ${member.role === 'member' ? 'selected' : ''}>Member</option>
                    <option value="viewer" ${member.role === 'viewer' ? 'selected' : ''}>Viewer</option>
                </select>
            </td>
            <td>${formatDate(member.joined_at)}</td>
            <td>
                ${member.role !== 'owner' ? `
                    <button class="btn btn-ghost btn-sm btn-danger remove-member" data-user-id="${member.user_id}">
                        Remove
                    </button>
                ` : ''}
            </td>
        </tr>
    `).join('');

    // Role change handlers
    tbody.querySelectorAll('.role-select').forEach(select => {
        select.addEventListener('change', async (e) => {
            const userId = (e.target as HTMLSelectElement).dataset.userId!;
            const newRole = (e.target as HTMLSelectElement).value;
            await handleRoleChange(userId, newRole);
        });
    });

    // Remove handlers
    tbody.querySelectorAll('.remove-member').forEach(btn => {
        btn.addEventListener('click', async (e) => {
            const userId = (e.target as HTMLElement).dataset.userId!;
            await handleRemoveMember(userId);
        });
    });
}

async function handleRoleChange(userId: string, newRole: string): Promise<void> {
    if (!selectedOrg) return;

    try {
        await api.updateMemberRole(selectedOrg.id, userId, newRole);
        await loadMembers();
    } catch (error) {
        console.error('Failed to update role:', error);
        alert('Failed to update role');
        await loadMembers();
    }
}

async function handleRemoveMember(userId: string): Promise<void> {
    if (!selectedOrg) return;

    if (!confirm('Remove this member from the organization?')) return;

    try {
        await api.removeMember(selectedOrg.id, userId);
        await loadMembers();
        updateOrgStats();
    } catch (error) {
        console.error('Failed to remove member:', error);
        alert('Failed to remove member');
    }
}

// ============================================================================
// Invitations
// ============================================================================

async function loadInvitations(): Promise<void> {
    if (!selectedOrg) return;

    try {
        const response = await api.getInvitations(selectedOrg.id);
        currentInvitations = response.invitations;
        renderInvitations();
    } catch (error) {
        console.error('Failed to load invitations:', error);
        currentInvitations = [];
        renderInvitations();
    }
}

function renderInvitations(): void {
    const tbody = document.getElementById('invitations-tbody')!;
    const noInvitations = document.getElementById('no-invitations')!;

    if (currentInvitations.length === 0) {
        tbody.innerHTML = '';
        noInvitations.classList.remove('hidden');
        return;
    }

    noInvitations.classList.add('hidden');

    tbody.innerHTML = currentInvitations.map(inv => `
        <tr>
            <td>${escapeHtml(inv.email)}</td>
            <td><span class="badge">${inv.role}</span></td>
            <td>${formatDate(inv.invited_at)}</td>
            <td>${formatDate(inv.expires_at)}</td>
            <td>
                <button class="btn btn-ghost btn-sm btn-danger revoke-invite" data-invite-id="${inv.id}">
                    Revoke
                </button>
            </td>
        </tr>
    `).join('');

    // Revoke handlers
    tbody.querySelectorAll('.revoke-invite').forEach(btn => {
        btn.addEventListener('click', async (e) => {
            const inviteId = (e.target as HTMLElement).dataset.inviteId!;
            await handleRevokeInvite(inviteId);
        });
    });
}

async function handleRevokeInvite(inviteId: string): Promise<void> {
    if (!selectedOrg) return;

    if (!confirm('Revoke this invitation?')) return;

    try {
        await api.revokeInvitation(selectedOrg.id, inviteId);
        await loadInvitations();
        updateOrgStats();
    } catch (error) {
        console.error('Failed to revoke invitation:', error);
        alert('Failed to revoke invitation');
    }
}

function showInviteModal(): void {
    (document.getElementById('invite-email') as HTMLInputElement).value = '';
    (document.getElementById('invite-role') as HTMLSelectElement).value = 'member';
    inviteModal.classList.remove('hidden');
}

function hideInviteModal(): void {
    inviteModal.classList.add('hidden');
}

async function handleInvite(e: Event): Promise<void> {
    e.preventDefault();
    if (!selectedOrg) return;

    const email = (document.getElementById('invite-email') as HTMLInputElement).value;
    const role = (document.getElementById('invite-role') as HTMLSelectElement).value;

    try {
        await api.inviteMember(selectedOrg.id, email, role);
        hideInviteModal();
        await loadInvitations();
        updateOrgStats();
    } catch (error) {
        console.error('Failed to send invitation:', error);
        alert('Failed to send invitation');
    }
}

// ============================================================================
// Policies
// ============================================================================

async function loadPolicies(): Promise<void> {
    if (!selectedOrg) return;

    try {
        currentPolicies = await api.getPolicies(selectedOrg.id);
        renderPolicies();
    } catch (error) {
        console.error('Failed to load policies:', error);
        currentPolicies = null;
    }
}

function renderPolicies(): void {
    if (!currentPolicies) return;

    (document.getElementById('policy-require-2fa') as HTMLInputElement).checked = currentPolicies.require_2fa;

    const providersSelect = document.getElementById('policy-providers') as HTMLSelectElement;
    Array.from(providersSelect.options).forEach(option => {
        option.selected = currentPolicies?.allowed_providers?.includes(option.value) ?? false;
    });

    (document.getElementById('policy-residency') as HTMLSelectElement).value = currentPolicies.data_residency || '';
    (document.getElementById('policy-storage') as HTMLInputElement).value = currentPolicies.max_storage_gb?.toString() || '';
}

async function handleSavePolicies(): Promise<void> {
    if (!selectedOrg) return;

    const require2fa = (document.getElementById('policy-require-2fa') as HTMLInputElement).checked;

    const providersSelect = document.getElementById('policy-providers') as HTMLSelectElement;
    const allowedProviders = Array.from(providersSelect.selectedOptions).map(o => o.value);

    const residency = (document.getElementById('policy-residency') as HTMLSelectElement).value || undefined;
    const storageInput = (document.getElementById('policy-storage') as HTMLInputElement).value;
    const maxStorage = storageInput ? parseInt(storageInput, 10) : undefined;

    try {
        await api.updatePolicies(selectedOrg.id, {
            require_2fa: require2fa,
            allowed_providers: allowedProviders.length > 0 ? allowedProviders : undefined,
            data_residency: residency,
            max_storage_gb: maxStorage
        });
        alert('Policies saved successfully');
    } catch (error) {
        console.error('Failed to save policies:', error);
        alert('Failed to save policies');
    }
}

// ============================================================================
// Create Organization
// ============================================================================

function showCreateOrgModal(): void {
    (document.getElementById('org-name') as HTMLInputElement).value = '';
    (document.getElementById('org-plan') as HTMLSelectElement).value = 'free';
    createOrgModal.classList.remove('hidden');
}

function hideCreateOrgModal(): void {
    createOrgModal.classList.add('hidden');
}

async function handleCreateOrg(e: Event): Promise<void> {
    e.preventDefault();

    const name = (document.getElementById('org-name') as HTMLInputElement).value;
    const plan = (document.getElementById('org-plan') as HTMLSelectElement).value;

    try {
        const newOrg = await api.createOrganization(name, plan);
        hideCreateOrgModal();
        organizations.push(newOrg);
        renderOrgList();
        showOrgDetail(newOrg);
    } catch (error) {
        console.error('Failed to create organization:', error);
        alert('Failed to create organization');
    }
}

// ============================================================================
// Utilities
// ============================================================================

function escapeHtml(str: string): string {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
}

function formatDate(isoDate: string): string {
    try {
        const date = new Date(isoDate);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
        });
    } catch {
        return isoDate;
    }
}
